import { useCallback, useMemo, useState } from "react";

import {
  CalendarView,
  SummaryCards,
  FlightListByDate,
  StatusBar,
  groupFlightsByDate,
  calculateSummaryStats,
  transformQuotesToFlights,
  getCurrentMonthDateRange,
  formatDateKey,
} from "@/features/track";
import { useFlights } from "@/features/track/hooks/useFlights";
import { useGroundStatusSweep } from "@/features/track/hooks/useGroundStatusSweep";
import FlightListSkeleton from "@/features/track/components/FlightListSkeleton";

const TrackView = () => {
  const [departureDateRange, setDepartureDateRange] = useState(() => {
    return getCurrentMonthDateRange();
  });
  const [selectedDate, setSelectedDate] = useState(null);

  const {
    data: flightsData,
    isLoading,
    error,
  } = useFlights(
    { dateRange: departureDateRange },
    { enabled: true, limit: 100 }
  );

  const rawFlights = useMemo(() => flightsData?.pages?.flat() || [], [flightsData]);

  const flights = useMemo(() => {
    if (!rawFlights.length) return [];
    return transformQuotesToFlights(rawFlights);
  }, [rawFlights]);

  // Background sweep: DELAYED + airborne detection for pre-flight flights
  useGroundStatusSweep(rawFlights);

  const flightsByDate = useMemo(() => {
    const grouped = groupFlightsByDate(flights);
    if (selectedDate) {
      const key = formatDateKey(selectedDate);
      return grouped[key] ? { [key]: grouped[key] } : {};
    }
    // Filter grouped flights to only include dates within the selected calendar range
    const startKey = formatDateKey(departureDateRange.startDate);
    const endKey = formatDateKey(departureDateRange.endDate);
    const filtered = {};
    Object.keys(grouped).forEach(key => {
      if (key >= startKey && key <= endKey) {
        filtered[key] = grouped[key];
      }
    });
    return filtered;
  }, [flights, selectedDate, departureDateRange]);

  // Flatten filtered flights for stats and counts
  const filteredFlights = useMemo(() => {
    return Object.values(flightsByDate).flat();
  }, [flightsByDate]);

  const summaryStats = useMemo(() => {
    return calculateSummaryStats(filteredFlights);
  }, [filteredFlights]);

  const maintenanceCount = useMemo(() => {
    return filteredFlights.filter((f) => f.status === "under_maintenance").length;
  }, [filteredFlights]);

  const handleDateRangeChange = useCallback((newDateRange) => {
    console.log('[handleDateRangeChange]', newDateRange?.viewMode, newDateRange?.startDate?.toISOString(), newDateRange?.endDate?.toISOString());
    setSelectedDate(newDateRange?.viewMode === "day" ? newDateRange.selectedDate : null);
    if (newDateRange?.startDate && newDateRange?.endDate) {
      setDepartureDateRange({
        startDate: newDateRange.startDate,
        endDate: newDateRange.endDate,
      });
    }
  }, []);

  return (
    <div>
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          Error loading flights: {error.message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6">
        <div className="flex flex-col gap-6 w-full lg:w-[360px] xl:w-[380px]">
          <div className="order-2 md:order-1">
            <CalendarView
              flights={flights}
              onDateRangeChange={handleDateRangeChange}
            />
          </div>

          <div className="order-1 md:order-2">
            <SummaryCards stats={summaryStats} />
          </div>

          <div className="order-3">
            <StatusBar maintenanceCount={maintenanceCount} />
          </div>
        </div>

        <div className="max-h-[calc(100vh-200px)] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
          {isLoading ? (
            <FlightListSkeleton />
          ) : (
            <div>
              <FlightListByDate
                flightsByDate={flightsByDate}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrackView;
